/**
 * /admin/exports · Live Exports Catalogue
 *
 * A single-screen dashboard for every download link Michael has
 * published to sister apps (Absorbo, and any future partner).
 * Shows title, filename, size, download count, last downloaded
 * timestamp, and lets him copy the share URL, edit metadata, or
 * revoke access.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Copy, Download, Trash2, RefreshCw, Pencil, Check, X,
  ChevronLeft, ExternalLink,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtBytes = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const b = Number(n);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

export default function AdminExports() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [copied, setCopied] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const auth = () => ({
    Authorization: `Bearer ${localStorage.getItem("reclaim_token")}`,
  });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/admin/exports`, { headers: auth() });
      if (!r.ok) throw new Error(r.status === 403 ? "Admin only." : `HTTP ${r.status}`);
      const data = await r.json();
      setItems(data.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const shareUrl = (key) => `${API}/api/exports/${key}`;

  const copyShare = async (key) => {
    try {
      await navigator.clipboard.writeText(shareUrl(key));
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* ignore */ }
  };

  const startEdit = (item) => {
    setEditingKey(item.key);
    setEditDraft({ title: item.title || "", description: item.description || "" });
  };

  const saveEdit = async (key) => {
    setBusyKey(key);
    try {
      const r = await fetch(`${API}/api/admin/exports/${key}`, {
        method: "PATCH",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify(editDraft),
      });
      if (!r.ok) throw new Error(await r.text());
      setEditingKey(null);
      await load();
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setBusyKey(null);
    }
  };

  const revoke = async (item) => {
    const ok = window.confirm(
      `Revoke access to “${item.title || item.filename}”?\n\n` +
      `The share URL will immediately return 404. The underlying file in Object Storage is preserved — you can re-publish it later.\n\n` +
      `Downloads so far: ${item.download_count || 0}.`
    );
    if (!ok) return;
    setBusyKey(item.key);
    try {
      const r = await fetch(`${API}/api/admin/exports/${item.key}`, {
        method: "DELETE",
        headers: auth(),
      });
      if (!r.ok) throw new Error(await r.text());
      await load();
    } catch (e) {
      alert(`Revoke failed: ${e.message}`);
    } finally {
      setBusyKey(null);
    }
  };

  const forceSync = async (key) => {
    setBusyKey(key);
    try {
      const r = await fetch(`${API}/api/admin/exports/${key}/sync-now`, {
        method: "POST",
        headers: auth(),
      });
      if (!r.ok) throw new Error(await r.text());
      await load();
    } catch (e) {
      alert(`Sync failed: ${e.message}`);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: "radial-gradient(ellipse at top, #14201C 0%, #0A100E 60%, #050807 100%)",
        color: "#F5E7C3",
        fontFamily: '"Manrope", sans-serif',
      }}
      data-testid="admin-exports-page"
    >
      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Back */}
        <button
          onClick={() => nav("/admin")}
          className="flex items-center gap-1 text-[10px] uppercase opacity-50 hover:opacity-90 transition mb-8"
          style={{ letterSpacing: "0.3em", color: "#D7BB7A" }}
          data-testid="admin-exports-back"
        >
          <ChevronLeft size={14} /> Admin
        </button>

        <div className="flex items-baseline justify-between mb-8">
          <div>
            <p className="uppercase tracking-[0.4em] text-[10px] opacity-60" style={{ color: "#D7BB7A" }}>
              Cross-App
            </p>
            <h1
              className="italic mt-2"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
                fontWeight: 400,
              }}
            >
              Live Exports Catalogue
            </h1>
            <p className="text-sm opacity-60 mt-2 max-w-xl italic">
              Every download link you&apos;ve published to a sister app. Copy, edit, or revoke access — the underlying files in Object Storage are always preserved.
            </p>
          </div>
          <button
            onClick={load}
            className="text-xs opacity-60 hover:opacity-100 flex items-center gap-1.5"
            data-testid="admin-exports-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        {loading && <div className="text-sm opacity-50">Loading…</div>}
        {error && (
          <div className="text-sm text-red-400/80 border border-red-500/30 bg-red-500/10 px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="text-sm opacity-50 italic">No exports published yet.</div>
        )}

        <div className="space-y-4" data-testid="admin-exports-list">
          {items.map((item) => {
            const url = shareUrl(item.key);
            const isEditing = editingKey === item.key;
            const isBusy = busyKey === item.key;
            const isMusicSync = (item.filename || "").includes("absorbo-music");

            return (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl p-5 border transition-all"
                style={{
                  background: "rgba(15, 27, 23, 0.5)",
                  borderColor: "rgba(215, 187, 122, 0.20)",
                  backdropFilter: "blur(6px)",
                  WebkitBackdropFilter: "blur(6px)",
                  opacity: isBusy ? 0.6 : 1,
                }}
                data-testid={`export-row-${item.key}`}
              >
                {/* Title + edit inline */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <>
                        <input
                          value={editDraft.title}
                          onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
                          placeholder="Title"
                          className="w-full bg-black/40 border border-amber-500/30 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-amber-500/70"
                          data-testid={`export-edit-title-${item.key}`}
                        />
                        <textarea
                          value={editDraft.description}
                          onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })}
                          placeholder="Description"
                          rows={2}
                          className="w-full mt-2 bg-black/40 border border-amber-500/30 rounded-lg px-3 py-2 text-sm text-white/80 outline-none focus:border-amber-500/70 resize-none"
                          data-testid={`export-edit-desc-${item.key}`}
                        />
                      </>
                    ) : (
                      <>
                        <div
                          className="italic font-normal text-lg"
                          style={{ fontFamily: '"Cormorant Garamond", serif', color: "#F5E7C3" }}
                        >
                          {item.title || item.filename}
                        </div>
                        {item.description && (
                          <div className="text-xs opacity-60 mt-1 leading-relaxed">
                            {item.description}
                          </div>
                        )}
                        <div className="text-[10px] opacity-40 mt-1.5 font-mono truncate">
                          {item.filename}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Right-side stats */}
                  <div className="text-right flex-shrink-0">
                    <div className="text-2xl font-light" style={{ color: "#D7BB7A" }}>
                      {item.download_count || 0}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider opacity-50">
                      downloads
                    </div>
                    <div className="text-[10px] opacity-40 mt-2">
                      last: {fmtDate(item.last_downloaded_at)}
                    </div>
                  </div>
                </div>

                {/* Share URL row */}
                <div className="mt-4 flex items-center gap-2">
                  <code
                    className="flex-1 text-[10px] font-mono px-3 py-2 rounded-lg truncate"
                    style={{ background: "rgba(0,0,0,0.35)", color: "#8FA69B" }}
                  >
                    {url}
                  </code>
                  <button
                    onClick={() => copyShare(item.key)}
                    className="px-3 py-2 rounded-lg text-xs flex items-center gap-1.5 transition-all border"
                    style={{
                      background: copied === item.key ? "rgba(215, 187, 122, 0.25)" : "rgba(215, 187, 122, 0.10)",
                      borderColor: "rgba(215, 187, 122, 0.30)",
                      color: "#F5E7C3",
                    }}
                    data-testid={`export-copy-${item.key}`}
                  >
                    {copied === item.key ? <Check size={12} /> : <Copy size={12} />}
                    {copied === item.key ? "Copied" : "Copy"}
                  </button>
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-2 rounded-lg text-xs flex items-center gap-1.5 transition-all border hover:opacity-90"
                    style={{
                      background: "rgba(215, 187, 122, 0.10)",
                      borderColor: "rgba(215, 187, 122, 0.30)",
                      color: "#F5E7C3",
                    }}
                    data-testid={`export-open-${item.key}`}
                  >
                    <ExternalLink size={12} /> Open
                  </a>
                </div>

                {/* Meta strip */}
                <div className="mt-4 flex items-center gap-4 text-[10px] uppercase tracking-wider opacity-50">
                  <span>{fmtBytes(item.size_bytes)}</span>
                  <span>·</span>
                  <span>{item.content_type || "unknown"}</span>
                  <span>·</span>
                  <span>created {fmtDate(item.created_at)}</span>
                  {item.last_synced && (
                    <>
                      <span>·</span>
                      <span>synced {fmtDate(item.last_synced)}</span>
                    </>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-4 flex items-center gap-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => saveEdit(item.key)}
                        disabled={isBusy}
                        className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 border transition-all"
                        style={{ background: "rgba(215, 187, 122, 0.20)", borderColor: "rgba(215, 187, 122, 0.40)", color: "#F5E7C3" }}
                        data-testid={`export-save-${item.key}`}
                      >
                        <Check size={12} /> Save
                      </button>
                      <button
                        onClick={() => setEditingKey(null)}
                        className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 opacity-60 hover:opacity-100"
                        data-testid={`export-cancel-${item.key}`}
                      >
                        <X size={12} /> Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => startEdit(item)}
                        className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 opacity-70 hover:opacity-100 border border-transparent hover:border-amber-500/30"
                        data-testid={`export-edit-${item.key}`}
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      {isMusicSync && (
                        <button
                          onClick={() => forceSync(item.key)}
                          disabled={isBusy}
                          className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 opacity-70 hover:opacity-100 border border-transparent hover:border-amber-500/30"
                          data-testid={`export-sync-${item.key}`}
                        >
                          <RefreshCw size={12} /> Sync now
                        </button>
                      )}
                      <button
                        onClick={() => revoke(item)}
                        disabled={isBusy}
                        className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 opacity-50 hover:opacity-100 border border-transparent hover:border-red-500/40 hover:text-red-300 ml-auto"
                        data-testid={`export-revoke-${item.key}`}
                      >
                        <Trash2 size={12} /> Revoke
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
